import {
    ChangeDetectorRef, ContentChild,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from "@angular/core";
import {ControlValueAccessor, FormControl} from "@angular/forms";
import {FUIIconComponent} from "../../icons/icon/icon.component";
import {Subscription} from "rxjs";

@Directive()
export class FUIAbstractInput implements ControlValueAccessor, OnDestroy {
    @Input() public name: string;
    @Input() public label: string;
    @Input() public labelI18NParams: any = {};
    @Input() public notation: string;
    @Input() public addable: boolean;
    @Input() public forceRequired: boolean;
    @HostBinding("class.extended") @Input() public extendable: boolean;
    @Input() public loading: boolean;

    @Output() public added$: EventEmitter<boolean>;
    @Output() public extended$: EventEmitter<boolean>;
    @Output() public focussed$: EventEmitter<boolean>;
    @Output() public valueChange: EventEmitter<any>;
    @Output() public removed$: EventEmitter<boolean> = new EventEmitter<boolean>();

    @HostBinding("class.focussed") public focussed: boolean;
    @HostBinding("class.hovered") public hovered: boolean;
    @HostBinding("class.valued") public valued: boolean;
    @HostBinding("class.removed") public removed: boolean;

    @ContentChild(FUIIconComponent) icon: FUIIconComponent
    @ViewChild("input", {read: ElementRef}) public input: ElementRef;

    public tabindex: number;
    public required: boolean;
    public hasValidators: boolean;

    protected _value: any;
    protected destroyed: boolean = false;

    controlSubscriptions: Subscription;

    private _control: FormControl;
    private _disabled: boolean;
    private _placeholder: string;

    constructor(protected changeDetectorRef: ChangeDetectorRef) {
        this._control = new FormControl();
        this.focussed$ = new EventEmitter<boolean>();
        this.valueChange = new EventEmitter<any>();
    }

    @Input()
    get placeholder(): string {
        return this._placeholder == undefined ? "''" : this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
    }

    /**
     *
     * @returns {boolean}
     */
    get disabled(): boolean {
        return this._disabled;
    }

    /**
     *
     * @param {boolean} disabled
     */
    @HostBinding("class.disabled")
    @Input()
    set disabled(disabled: boolean) {
        this._disabled = disabled;
        if (this.disabled) {
            this.tabindex = -1;
        } else {
            this.tabindex = 0;
        }
    }

    /**
     *
     * @returns {string}
     */
    get value(): any {
        return this._value;
    }

    valueEqual(value1: any, value2: any): boolean {
        return (this.isEmptyArray(value1) && this.isEmptyArray(value2)) || value1 == value2;
    }

    isEmptyArray(value: any): boolean {
        return (!!value) && (value.constructor === Array) && value.length === 0;
    }

    /**
     *
     * @param value
     */
    @Input()
    set value(value: any) {
        if (!this.valueEqual(value, this._value)) {
            if (value === undefined) {
                value = null;
            }
            this._value = value;
            this.determineValued(this._value);
            this.onChange(value);
            this.valueChange.emit(value);
        }
    }

    protected determineValued(value: any) {
        this.valued = (value != undefined);
    }

    get control(): FormControl {
        return this._control;
    }

    @Input()
    set control(value: FormControl) {
        if (value !== this._control) {
            this._control = value;

            if (this.controlSubscriptions) {
                this.controlSubscriptions.unsubscribe();
            }

            this.controlSubscriptions = new Subscription();

            // check if the control has any validators
            setTimeout(() => {
                if (this.control) {
                    if (this.control["required$"]) {
                        this.controlSubscriptions.add(this.control["required$"].subscribe((isRequired) => queueMicrotask(() => {
                            this.required = isRequired;
                            this.changeDetectorRef.markForCheck();
                        })));
                    }
                    if (this.control["validators$"]) {
                        this.controlSubscriptions.add(this.control["validators$"].subscribe((hasValidators) => queueMicrotask(() => {
                            this.hasValidators = hasValidators;
                            this.changeDetectorRef.markForCheck();
                        })));
                    } else {
                        this.hasValidators = this.control.validator != undefined;
                        this.changeDetectorRef.markForCheck();
                    }
                } else {
                    this.hasValidators = false;
                    this.changeDetectorRef.markForCheck();
                }
            });
        }
    }

    /**
     *
     */
    @HostListener("focus")
    public onInputComponentFocus(): void {
        focus();
    }

    /**
     *
     */
    @HostListener("blur")
    public onInputComponentBlur(): void {
    }

    /**
     *
     */
    public focus(): void {
        if (this.input && !this.disabled && !this.focussed && this.input.nativeElement.focus) {
            this.input.nativeElement.focus();
        }
    }

    /**
     * Select all the text in the input
     */
    public selectAll(): void {
        if (this.input && !this.disabled && this.focussed && this.input.nativeElement.select) {
            this.input.nativeElement.select();
        }
    }

    /**
     *
     */
    public click(): void {
        if (this.input && !this.disabled) {
            this.input.nativeElement.click();
        }
    }

    /**
     * When focus is removed from the input element
     * @param event
     */
    public onBlur(event?: any): void {
        this.focussed = false;
        this.focussed$.emit(this.focussed);
    }

    /**
     * When focus is detected on the input element
     * @param event
     */
    public onFocus(event?: any): void {
        this.focussed = true;
        this.focussed$.emit(this.focussed);
    }

    /**
     *
     * @param _
     */
    public onChange = (_: any) => {

    };

    /**
     *
     */
    public onTouched = () => {

    };

    @HostListener("mouseenter")
    public mouseenter(): void {
        this.hovered = true;
    }

    @HostListener("mouseleave")
    public mouseleave(): void {
        this.hovered = false;
    }

    /**
     *
     * @param value
     */
    public writeValue(value: any): void {
        if (!this.destroyed) {
            this._value = value;
            this.determineValued(this._value);
            this.changeDetectorRef.detectChanges();
        }
    }

    /**
     *
     * @param fn
     */
    public registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    /**
     *
     * @param fn
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Emit remove event
     * @param event
     */
    remove(): void {
        this.removed = true;
        this.removed$.emit(this.removed);
    }

    get validateable(): boolean {
        return this.forceRequired || this.required || this.hasValidators;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.controlSubscriptions) {
            this.controlSubscriptions.unsubscribe();
        }
    }
}
